import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ICandidateProductStatus,
  ICompareProductState,
} from "../types/compare-product";
import { ISpecificationCandidateProductResponse } from "@/shared/services/interface/responses/ISpecificationResponse";
import { ISupabaseCompareItemAnswerResponse } from "@/shared/services/interface/responses/ISupabaseCompare";
import { SpecificationItemAnswerStatus } from "@/shared/enums/specification-item-answer";

const initialState: ICompareProductState = {
  candidateProducts: [],
  productStatus: [],
  specificationItemAnswers: [],
  removedItems: [],
  skeletons: [],
  selectedCategories: [],
  activeProduct: null,
  filterAnswer: SpecificationItemAnswerStatus.ALL,
  currentCarouselIndex: 0,
  filter: {
    filtersExist: false,
    idsToShowAfterFilter: [],
  },
  expandedItems: {},
  showHeatmap: false,
};

// 🚀 Helper function for optimized item answer updates
const updateItemAnswerOptimized = (
  currentAnswers: ISupabaseCompareItemAnswerResponse[],
  newAnswer: ISupabaseCompareItemAnswerResponse
): ISupabaseCompareItemAnswerResponse[] => {
  // Find existing answer by index for O(1) update instead of O(n) map
  const existingIndex = currentAnswers.findIndex(
    (answer) =>
      answer.productId === newAnswer.productId &&
      answer.specificationItemId === newAnswer.specificationItemId
  );

  if (existingIndex !== -1) {
    // Direct index update - much faster than mapping entire array
    const updatedAnswers = [...currentAnswers];
    updatedAnswers[existingIndex] = newAnswer;
    return updatedAnswers;
  } else {
    // Only add if not exists
    return [...currentAnswers, newAnswer];
  }
};

// 🚀 Helper function for batch updates
const batchUpdateItemAnswers = (
  currentAnswers: ISupabaseCompareItemAnswerResponse[],
  newAnswers: ISupabaseCompareItemAnswerResponse[]
): ISupabaseCompareItemAnswerResponse[] => {
  if (newAnswers.length === 0) return currentAnswers;

  // Create a Map for O(1) lookups
  const answerMap = new Map(
    currentAnswers.map((answer, index) => [
      `${answer.productId}:${answer.specificationItemId}`,
      { answer, index },
    ])
  );

  // Track which indices were updated
  const updatedIndices = new Set<number>();
  const resultAnswers = [...currentAnswers];

  // Process new answers
  newAnswers.forEach((newAnswer) => {
    const key = `${newAnswer.productId}:${newAnswer.specificationItemId}`;
    const existing = answerMap.get(key);

    if (existing) {
      // Update existing answer
      resultAnswers[existing.index] = newAnswer;
      updatedIndices.add(existing.index);
    } else {
      // Add new answer
      resultAnswers.push(newAnswer);
    }
  });

  return resultAnswers;
};

export const compareProductSlice = createSlice({
  name: "compareProduct",
  initialState,
  reducers: {
    setCandidateProducts: (
      state,
      action: PayloadAction<ISpecificationCandidateProductResponse[] | []>
    ) => {
      state.candidateProducts = action.payload;
    },
    setFilterAnswer: (
      state,
      action: PayloadAction<SpecificationItemAnswerStatus>
    ) => {
      state.filterAnswer = action.payload;
    },
    setSpecificationItemAnswers: (
      state,
      action: PayloadAction<ISupabaseCompareItemAnswerResponse[]>
    ) => {
      state.specificationItemAnswers = action.payload;
    },
    setRemovedItems: (state, action: PayloadAction<string[]>) => {
      state.removedItems = action.payload;
    },
    // 🚀 OPTIMIZED: Single item answer update
    addSpecificationItemAnswer: (
      state,
      action: PayloadAction<ISupabaseCompareItemAnswerResponse>
    ) => {
      // Use optimized helper function
      state.specificationItemAnswers = updateItemAnswerOptimized(
        state.specificationItemAnswers,
        action.payload
      );

      // 🚀 OPTIMIZED: Only update activeProduct if it's actually different AND meaningful
      // Don't trigger unnecessary re-renders for activeProduct updates
      const newProductId = action.payload?.productId;
      if (newProductId && state.activeProduct !== newProductId) {
        // Only set if this is a meaningful change (e.g., user interaction)
        const isUserTriggered =
          action.payload.status !== SpecificationItemAnswerStatus.SEARCHING;
        if (isUserTriggered) {
          state.activeProduct = newProductId;
        }
      }
    },
    // 🚀 NEW: Batch update multiple item answers at once
    batchAddSpecificationItemAnswers: (
      state,
      action: PayloadAction<ISupabaseCompareItemAnswerResponse[]>
    ) => {
      state.specificationItemAnswers = batchUpdateItemAnswers(
        state.specificationItemAnswers,
        action.payload
      );

      // Set activeProduct to the last meaningful update
      const lastMeaningfulUpdate = action.payload
        .filter(
          (answer) => answer.status !== SpecificationItemAnswerStatus.SEARCHING
        )
        .pop();

      if (
        lastMeaningfulUpdate?.productId &&
        state.activeProduct !== lastMeaningfulUpdate.productId
      ) {
        state.activeProduct = lastMeaningfulUpdate.productId;
      }
    },
    // 🚀 NEW: Update item answer without triggering activeProduct change
    updateSpecificationItemAnswerSilent: (
      state,
      action: PayloadAction<ISupabaseCompareItemAnswerResponse>
    ) => {
      state.specificationItemAnswers = updateItemAnswerOptimized(
        state.specificationItemAnswers,
        action.payload
      );
      // No activeProduct update - for background/system updates
    },
    cleanSpecificationItemAnswers: (state) => {
      state.specificationItemAnswers = [];
      // Reset activeProduct when clearing all answers
      state.activeProduct = null;
    },
    addCandidateProduct: (state, action: PayloadAction<any>) => {
      state.candidateProducts = [...state.candidateProducts, action.payload];
    },
    setSkeleton: (
      state,
      action: PayloadAction<ISpecificationCandidateProductResponse[]>
    ) => {
      state.skeletons = action.payload;
    },
    removeCandidateProduct: (state, action: PayloadAction<string>) => {
      const removedProduct = state.candidateProducts.find(
        (product) => product.product_id === action.payload
      );
      if (removedProduct) {
        state.candidateProducts = state.candidateProducts.filter(
          (product) => product.product_id !== action.payload
        );
        state.skeletons = [...state.skeletons, removedProduct];

        // 🚀 Clear activeProduct if removed product was active
        if (state.activeProduct === action.payload) {
          state.activeProduct = null;
        }
      }
    },
    undoCandidateProduct: (
      state,
      action: PayloadAction<string | null | undefined>
    ) => {
      if (!action.payload) {
        if (state.skeletons.length == 0) return;
        const lastSkeleton = state.skeletons[state.skeletons.length - 1];
        state.candidateProducts = [...state.candidateProducts, lastSkeleton];
        state.skeletons = state.skeletons.filter(
          (product) => product.product_id !== lastSkeleton.product_id
        );
        return;
      }
      const removedProduct = state.skeletons.find(
        (product) => product.product_id === action.payload
      );
      if (removedProduct) {
        state.candidateProducts = [...state.candidateProducts, removedProduct];
        state.skeletons = state.skeletons.filter(
          (product) => product.product_id !== action.payload
        );
      }
    },
    cleanCandidateProducts: (state) => {
      state.candidateProducts = [];
      state.skeletons = [];
      state.productStatus = [];
      // 🚀 Clear activeProduct when cleaning all products
      state.activeProduct = null;
    },
    setSelectedCategories: (state, action: PayloadAction<string[]>) => {
      state.selectedCategories = action.payload;
    },
    setActiveProduct: (state, action: PayloadAction<string | null>) => {
      state.activeProduct = action.payload;
    },
    setCandidateProductStatus: (
      state,
      action: PayloadAction<ICandidateProductStatus>
    ) => {
      // 🚀 Optimized candidate products update
      const candidateIndex = state.candidateProducts.findIndex(
        (product) => product.product_id === action.payload.productId
      );

      if (candidateIndex !== -1) {
        state.candidateProducts[candidateIndex] = {
          ...state.candidateProducts[candidateIndex],
          status: action.payload.status,
        };

        // Sort only after status update
        state.candidateProducts.sort(
          (b, a) => (a?.score ?? 0) - (b?.score ?? 0)
        );
      }

      // 🚀 Optimized product status update
      const statusIndex = state.productStatus.findIndex(
        (status) => status.productId === action.payload.productId
      );

      if (statusIndex !== -1) {
        state.productStatus[statusIndex] = action.payload;
      } else {
        state.productStatus = [...state.productStatus, action.payload];
      }
    },
    setCurrentCarouselIndex: (state, action: PayloadAction<number>) => {
      state.currentCarouselIndex = action.payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{
        filtersExist: boolean;
        idsToShowAfterFilter: string[];
      }>
    ) => {
      state.filter = action.payload;
    },
    // 🚀 NEW: Expand item state management
    setItemExpanded: (
      state,
      action: PayloadAction<{
        itemId: string;
        isExpanded: boolean;
        height?: number;
      }>
    ) => {
      const { itemId, isExpanded, height } = action.payload;
      state.expandedItems[itemId] = {
        isExpanded,
        height: height ?? state.expandedItems[itemId]?.height ?? 53, // Default height 53px
      };
    },
    // 🚀 NEW: Update item height
    setItemHeight: (
      state,
      action: PayloadAction<{
        itemId: string;
        height: number;
      }>
    ) => {
      const { itemId, height } = action.payload;
      if (state.expandedItems[itemId]) {
        state.expandedItems[itemId].height = height;
      }
    },
    // 🚀 NEW: Clear all expanded items
    clearExpandedItems: (state) => {
      state.expandedItems = {};
    },
    // 🚀 NEW: Toggle heatmap view
    toggleHeatmap: (state) => {
      state.showHeatmap = !state.showHeatmap;
    },
  },
});

export const {
  setCandidateProducts,
  setSkeleton,
  removeCandidateProduct,
  addCandidateProduct,
  undoCandidateProduct,
  cleanCandidateProducts,
  setSpecificationItemAnswers,
  addSpecificationItemAnswer,
  batchAddSpecificationItemAnswers,
  updateSpecificationItemAnswerSilent,
  cleanSpecificationItemAnswers,
  setRemovedItems,
  setSelectedCategories,
  setActiveProduct,
  setCandidateProductStatus,
  setFilterAnswer,
  setCurrentCarouselIndex,
  setFilter,
  setItemExpanded,
  setItemHeight,
  clearExpandedItems,
  toggleHeatmap,
} = compareProductSlice.actions;
export default compareProductSlice.reducer;
